<script>
import moment from "moment";
import {
  attendFlagList,
  cancelSourceList,
  checkinStatusList,
  downloadBlob,
  recordStatusList,
  registrationSourceList,
  RecordStatusEnum,
  findLabel,
} from "../help";
import {
  getTechActivityList,
  getRegistrationList,
  getRegistrationQuota,
  cancelSignUp,
  exportRegistrationList,
} from "@/api/techInnoActivities";

export default {
  name: "TechInnoActivityRegistration",
  data() {
    return {
      moment,
      checkinStatusList,
      RecordStatusEnum,
      // 活动下拉数据
      activityOptions: [],
      // 搜索条件
      searchForm: {
        // 活动（不选则查全部）
        activityId: undefined,
        // 手机号
        phone: "",
        // 姓名
        userName: "",
        // 公司
        companyName: "",
        // 签到状态
        checkinStatus: "",
      },
      // 报名时间段
      createTimeRange: [],
      columns: [
        { title: "活动名称", dataIndex: "activityName", key: "activityName", width: 180, ellipsis: true },
        { title: "板块", dataIndex: "sectionName", key: "sectionName", width: 110, ellipsis: true },
        { title: "活动类型", dataIndex: "typeName", key: "typeName", width: 110, ellipsis: true },
        { title: "姓名", dataIndex: "userName", key: "userName", width: 110, ellipsis: true },
        { title: "企业", dataIndex: "companyName", key: "companyName", width: 170, ellipsis: true },
        { title: "手机号", dataIndex: "phone", key: "phone", width: 120, ellipsis: true },
        { title: "报名身份", dataIndex: "identityName", key: "identityName", width: 110, ellipsis: true, customRender: (text) => text || "--" },
        {
          title: "来源",
          key: "source",
          width: 110,
          customRender: (text, record) => findLabel(registrationSourceList, record.source) || "--",
        },
        {
          title: "是否参加",
          key: "attendStatus",
          width: 100,
          ellipsis: true,
          // 仅邀约记录返回（0未确认 1参加 2不参加），普通报名为 null
          customRender: (text, record) =>
            record.attendStatus != null ? findLabel(attendFlagList, record.attendStatus) || "--" : "--",
        },
        { title: "推荐人", dataIndex: "referrer", key: "referrer", width: 110, ellipsis: true, customRender: (text) => text || "--" },
        { title: "报名时间", dataIndex: "createTime", key: "createTime", width: 170 },
        {
          title: "签到状态",
          key: "checkinStatus",
          width: 110,
          customRender: (text, record) => {
            // 活动无需签到时不展示签到状态（checkinStatus 恒为 0 但不渲染）
            if (record.needCheckin === 0) return "--";
            return findLabel(checkinStatusList, record.checkinStatus) || "--";
          },
        },
        {
          title: "签到时间",
          key: "checkinTime",
          width: 170,
          customRender: (text, record) => {
            // 活动无需签到时不展示签到时间
            if (record.needCheckin === 0) return "--";
            return record.checkinTime || "--";
          },
        },
        {
          title: "报名状态",
          key: "status",
          width: 100,
          customRender: (text, record) => findLabel(recordStatusList, record.status) || "--",
        },
        { title: "取消时间", dataIndex: "cancelTime", key: "cancelTime", width: 170, customRender: (text) => text || "--" },
        {
          title: "取消来源",
          key: "cancelSource",
          width: 100,
          customRender: (text, record) => {
            if (record.status !== RecordStatusEnum.CANCELED) return "--";
            return findLabel(cancelSourceList, record.cancelSource) || "--";
          },
        },
        { title: "操作", key: "action", width: 130, fixed: "right", scopedSlots: { customRender: "action" } },
      ],
      dataInfo: [],
      listLoading: false,
      // 统计信息条（选定活动后展示，接口实时计算）
      quota: null,
      // 导出 loading
      exportLoading: false,
      pagination: {
        pageSizeOptions: ["10", "20", "30", "50", "100"],
        showSizeChanger: true,
        current: 1,
        pageSize: 10,
        total: 0,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
      },
    };
  },
  created() {
    this.getActivityOptions();
    this.getList();
  },
  mounted() {
    // 宽屏下表格无横向滚动，取消操作列固定，避免固定列 overlay 产生空白列
    this.handleResize = () => {
      const wide = this.$el.clientWidth >= 2320;
      const cols = this.columns;
      cols[cols.length - 1].fixed = wide ? "" : "right";
    };
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    /**
     * @Description：活动下拉数据
     * @Author cxd
     * @Date 2026/10/26
     */
    async getActivityOptions() {
      try {
        const res = await getTechActivityList({ page: 1, size: 999 });
        if (String(res.code) === "200") {
          this.activityOptions = (res.data && res.data.items) || [];
        }
      } catch (e) {
        this.activityOptions = [];
      }
    },
    /**
     * @Description：报名列表（接口 page 从 1 开始）
     * @Author cxd
     * @Date 2026/10/26
     */
    async getList() {
      const params = {
        activityId: this.searchForm.activityId,
        phone: this.searchForm.phone,
        userName: this.searchForm.userName,
        companyName: this.searchForm.companyName,
        checkinStatus: this.searchForm.checkinStatus,
        page: this.pagination.current,
        size: this.pagination.pageSize,
      };
      if (this.createTimeRange.length) {
        params.createTimeStart = moment(this.createTimeRange[0]).format("YYYY-MM-DD") + " 00:00:00";
        params.createTimeEnd = moment(this.createTimeRange[1]).format("YYYY-MM-DD") + " 23:59:59";
      }
      this.listLoading = true;
      try {
        const res = await getRegistrationList(params);
        if (String(res.code) === "200") {
          this.dataInfo = (res.data && res.data.items) || [];
          this.pagination.total = (res.data && res.data.total) || 0;
        } else {
          this.$message.error(res.msg || "查询失败");
        }
      } catch (e) {
        this.$message.error("查询失败");
      } finally {
        this.listLoading = false;
        this.fetchQuota();
      }
    },
    /**
     * @Description：统计信息条（选定具体活动后拉取；未选活动或接口异常时不展示）
     * @Author cxd
     * @Date 2026/10/31
     */
    async fetchQuota() {
      if (!this.searchForm.activityId) {
        this.quota = null;
        return;
      }
      try {
        const res = await getRegistrationQuota(this.searchForm.activityId);
        this.quota = String(res.code) === "200" ? res.data : null;
      } catch (e) {
        this.quota = null;
      }
    },
    /**
     * @Description：查询
     * @Author cxd
     * @Date 2026/10/26
     */
    search() {
      this.pagination.current = 1;
      this.getList();
    },
    /**
     * @Description：重置
     * @Author cxd
     * @Date 2026/10/26
     */
    resetSearchForm() {
      this.searchForm = {
        activityId: undefined,
        phone: "",
        userName: "",
        companyName: "",
        checkinStatus: "",
      };
      this.createTimeRange = [];
      this.pagination.current = 1;
      this.pagination.pageSize = 10;
      this.getList();
    },
    /**
     * @Description：分页
     * @Author cxd
     * @Date 2026/10/26
     */
    pageChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getList();
    },
    /**
     * @Description：报名列表导出（按当前筛选条件，口径同列表；条件全空=全量）
     * @Author cxd
     * @Date 2026/10/31
     */
    async handleExport() {
      this.exportLoading = true;
      try {
        const params = {
          activityId: this.searchForm.activityId,
          phone: this.searchForm.phone,
          userName: this.searchForm.userName,
          companyName: this.searchForm.companyName,
          checkinStatus: this.searchForm.checkinStatus,
        };
        if (this.createTimeRange.length) {
          params.createTimeStart = moment(this.createTimeRange[0]).format("YYYY-MM-DD") + " 00:00:00";
          params.createTimeEnd = moment(this.createTimeRange[1]).format("YYYY-MM-DD") + " 23:59:59";
        }
        const res = await exportRegistrationList(params);
        downloadBlob(res, `报名列表-${moment().format("YYYYMMDDHHmmss")}.xlsx`);
      } catch (e) {
        this.$message.error("导出失败，请稍后重试");
      } finally {
        this.exportLoading = false;
      }
    },
    /**
     * @Description：取消报名（管理端不受活动时间、签到状态限制，接口支持批量）
     * @Author cxd
     * @Date 2026/10/26
     */
    handleCancelSignUp(record) {
      const that = this;
      this.$confirm({
        title: "确认取消该用户报名？",
        content: "管理端取消报名不受活动开始时间及签到状态限制，取消后不可恢复",
        okText: "确认",
        cancelText: "取消",
        async onOk() {
          try {
            const res = await cancelSignUp([record.id]);
            if (String(res.code) === "200") {
              that.$message.success(res.msg || "操作成功");
              that.getList();
            } else {
              that.$message.error(res.msg || "操作失败");
            }
          } catch (e) {
            that.$message.error("操作失败");
          }
        },
        onCancel() {},
      });
    },
  },
};
</script>

<template>
  <div class="tech-inno-registration">
    <ds-header title="报名列表"></ds-header>
    <a-form layout="inline" style="margin-bottom: 10px">
      <a-form-item label="活动：">
        <a-select
          v-model="searchForm.activityId"
          placeholder="请选择活动"
          style="width: 220px"
          allowClear
          show-search
          option-filter-prop="children"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option
            v-for="item in activityOptions"
            :key="item.id"
            :value="String(item.id)"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="手机号：">
        <a-input
          v-model.trim="searchForm.phone"
          placeholder="请输入手机号"
          style="width: 160px"
        />
      </a-form-item>
      <a-form-item label="姓名：">
        <a-input v-model.trim="searchForm.userName" placeholder="请输入姓名" style="width: 130px" />
      </a-form-item>
      <a-form-item label="公司：">
        <a-input
          v-model.trim="searchForm.companyName"
          placeholder="请输入公司"
          style="width: 160px"
        />
      </a-form-item>
      <a-form-item label="报名时间：">
        <a-range-picker
          v-model="createTimeRange"
          :placeholder="['开始时间', '结束时间']"
          format="YYYY-MM-DD"
          :getCalendarContainer="(triggerNode) => triggerNode.parentNode"
        />
      </a-form-item>
      <a-form-item label="签到状态：">
        <a-select
          v-model="searchForm.checkinStatus"
          placeholder="请选择"
          style="width: 150px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option value=""> 全部状态 </a-select-option>
          <a-select-option v-for="item in checkinStatusList" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <span>
        <a-button type="primary" style="margin-top: 3px" icon="search" @click="search">
          查询
        </a-button>
        <a-button style="margin-left: 15px" icon="redo" @click="resetSearchForm">重置</a-button>
      </span>
    </a-form>
    <div style="margin-top: 15px">
      <div class="toolbar">
        <a-button type="primary" icon="download" :loading="exportLoading" @click="handleExport">
          导出
        </a-button>
      </div>
      <!-- 统计信息条（选定活动后展示；取消报名等操作后随列表刷新重拉） -->
      <div v-if="quota" class="quota-bar">
        <span class="quota-name">{{ quota.activityName }}</span>
        <span>规模：{{ quota.capacity }}</span>
        <span>已报名：{{ quota.registeredCount }}</span>
        <span>已签到：{{ quota.checkedCount }}</span>
        <span>未签到：{{ quota.uncheckedCount }}</span>
        <span>签到率：{{ quota.signRate }}</span>
      </div>
      <a-table
        :rowKey="(record, index) => index"
        :columns="columns"
        :data-source="dataInfo"
        :loading="listLoading"
        :pagination="pagination.total ? pagination : false"
        :scroll="{ x: 2300 }"
        bordered
        @change="pageChange"
      >
        <span slot="action" slot-scope="text, record" class="action">
          <a-button
            v-if="record.status === RecordStatusEnum.VALID"
            type="link"
            class="btn"
            @click="handleCancelSignUp(record)"
          >
            取消报名
          </a-button>
        </span>
      </a-table>
    </div>
  </div>
</template>

<style scoped lang="scss">
.toolbar {
  margin-bottom: 10px;
}

.quota-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 8px 16px;
  margin-bottom: 10px;
  background: #f0f7ff;
  border: 1px solid #d6e8ff;
  border-radius: 4px;

  .quota-name {
    font-weight: 600;
  }
}

.action {
  // 操作按钮不允许换行，避免窄列下串行
  white-space: nowrap;
}

.btn {
  padding: 0;
}
</style>
